<template>
	<div class="CopilotAttachments" data-cy="copilot-attachments">
		<div v-if="attachments.length" class="CopilotAttachments__chips">
			<div v-for="attachment of attachments" :key="attachment.id"
				class="CopilotAttachments__chip" :class="`CopilotAttachments__chip--${attachment.status}`"
				data-cy="copilot-attachment" :data-kind="attachment.kind" :data-status="attachment.status"
			>
				<v-icon size="16" class="CopilotAttachments__icon">{{ icon(attachment) }}</v-icon>
				<span class="CopilotAttachments__name" :title="attachment.name">{{ attachment.name }}</span>
				<span class="CopilotAttachments__size">{{ formatSize(attachment.size) }}</span>
				<v-progress-circular v-if="attachment.status === 'uploading'" indeterminate size="14" width="2" />
				<span v-if="attachment.kind === 'other'" class="CopilotAttachments__note" data-cy="copilot-attachment-unsupported">{{ $t('text.copilotAttachmentUnsupported') }}</span>
				<span v-else-if="attachment.error" class="CopilotAttachments__error" data-cy="copilot-attachment-error">{{ attachment.error }}</span>
				<label v-if="attachment.kind === 'image'" class="CopilotAttachments__toggle" data-cy="copilot-attachment-images-toggle"
					:title="$t('text.copilotAttachmentAddToImagesConsent')"
				>
					<input type="checkbox" :checked="attachment.addToImages" :disabled="disabled || attachment.status === 'uploading'"
						@change="$emit('toggle-images', attachment.id, $event.target.checked)"
					>
					<span>
						{{ $t('text.copilotAttachmentAddToImages') }}
						<span class="CopilotAttachments__consent" data-cy="copilot-attachment-images-consent">{{ $t('text.copilotAttachmentAddToImagesConsent') }}</span>
					</span>
				</label>
				<v-btn icon="mdi-close" size="x-small" variant="text" density="compact" :disabled="disabled || attachment.status === 'uploading'"
					:aria-label="$t('text.copilotAttachmentRemove')" data-cy="copilot-attachment-remove" @click="$emit('remove', attachment.id)"
				/>
			</div>
		</div>
		<div v-for="(reject, index) of rejected" :key="`rej-${index}`" class="CopilotAttachments__rejected" data-cy="copilot-attachment-rejected">
			{{ reject.name }}: {{ $t(`text.copilotAttachmentRejected_${reject.reason}`) }}
		</div>
		<input ref="picker" type="file" multiple :accept="accept" class="CopilotAttachments__input" data-cy="copilot-attachment-input" @change="pick">
	</div>
</template>

<script lang="js">
// The attachment chips of the composer (ticket 07): one chip per file with its kind, size, the
// "add to Service images" toggle (images only, off until ticked; its text states that ticking uploads
// the image to the Service Provider's media library, with the provider as copyright owner, and
// confirms the usage rights) and a remove button, plus the hidden file input the
// drawer's attach button opens. Rejections of the last add (too many, too large, duplicate) are shown
// under the chips. State lives in the page adapter; everything here is emitted.
import { ACCEPT } from '@/copilot/copilot-attachments'

const ICONS = { pdf: 'mdi-file-pdf-box', image: 'mdi-image-outline', text: 'mdi-file-document-outline', other: 'mdi-file-cancel-outline' }

export default {
	name: 'CopilotAttachments',
	props: {
		/** PendingAttachment[] of copilot/copilot-attachments.ts */
		attachments: { type: Array, default: () => [] },
		/** RejectedFile[] of the last add */
		rejected: { type: Array, default: () => [] },
		disabled: { type: Boolean, default: false },
	},
	emits: [ 'add-files', 'remove', 'toggle-images' ],
	data: () => ({ accept: ACCEPT }),
	methods: {
		open() {
			this.$refs.picker?.click()
		},
		pick(event) {
			const files = Array.from(event.target.files ?? [])
			if (files.length) this.$emit('add-files', files)
			event.target.value = ''
		},
		icon(attachment) {
			return ICONS[attachment.kind] ?? ICONS.other
		},
		formatSize(bytes) {
			if (bytes < 1024) return `${bytes} B`
			if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
			return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
		},
	},
}
</script>

<style scoped lang="scss">
.CopilotAttachments {
	display: flex;
	flex-direction: column;
	gap: 4px;

	&__chips {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}
	&__chip {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px;
		padding: 4px 6px 4px 8px;
		border: 1px solid #e0e0e0;
		border-radius: 8px;
		font-size: 12px;
		background: #fafafa;
		&--uploaded { border-color: #43a047; }
		&--failed { border-color: #c62828; }
	}
	&__icon {
		color: #616161;
	}
	&__name {
		max-width: 160px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-weight: 600;
	}
	&__size {
		color: #757575;
	}
	&__note {
		color: #757575;
		font-style: italic;
	}
	&__error {
		color: #c62828;
	}
	&__toggle {
		display: flex;
		align-items: flex-start;
		gap: 4px;
		order: 1;
		flex-basis: 100%;
		cursor: pointer;
	}
	&__consent {
		display: block;
		color: #757575;
		font-size: 11px;
	}
	&__rejected {
		font-size: 12px;
		color: #c62828;
	}
	&__input {
		display: none;
	}
}
</style>
